import React, { useEffect, useRef, useState } from 'react'
import { Form, Modal, Select, Spin } from 'antd'
import { useTranslation } from 'react-i18next'

import type { ValidationDataset } from '../../../types/modelValidation'

interface ValidationDatasetImportModalProps {
    datasets: ValidationDataset[]
    loading: boolean
    onClose: () => void
    onConfirm: (sourceDatasetStableId: string) => void
    onSearch: (keyword: string) => void
    open: boolean
    saving: boolean
}

const ValidationDatasetImportModal: React.FC<
    ValidationDatasetImportModalProps
> = ({ datasets, loading, onClose, onConfirm, onSearch, open, saving }) => {
    const { t } = useTranslation('admin')
    const [sourceDatasetStableId, setSourceDatasetStableId] = useState<string>()
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (!open) {
            setSourceDatasetStableId(undefined)
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
            searchTimerRef.current = null
        }
    }, [open])

    useEffect(
        () => () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        },
        [],
    )

    const handleSearch = (keyword: string) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => {
            searchTimerRef.current = null
            onSearch(keyword.trim())
        }, 300)
    }

    return (
        <Modal
            cancelText={t('modelValidation.actions.cancel')}
            confirmLoading={saving}
            destroyOnClose
            maskClosable={false}
            okButtonProps={{ disabled: !sourceDatasetStableId || loading }}
            okText={t('modelValidation.actions.importDatasetImages')}
            onCancel={onClose}
            onOk={() => {
                if (sourceDatasetStableId) onConfirm(sourceDatasetStableId)
            }}
            open={open}
            title={t('modelValidation.datasets.importTitle')}
        >
            <Form layout="vertical">
                <Form.Item
                    label={t('modelValidation.datasets.importSource')}
                    required
                >
                    <Select
                        allowClear
                        filterOption={false}
                        loading={loading}
                        onChange={setSourceDatasetStableId}
                        onSearch={handleSearch}
                        notFoundContent={
                            loading ? (
                                <Spin size="small" />
                            ) : (
                                t('modelValidation.datasets.noImportCandidates')
                            )
                        }
                        options={datasets.map((dataset) => ({
                            label: `${dataset.name} · ${t(
                                `modelValidation.status.${dataset.status}`,
                            )}`,
                            value: dataset.stable_id,
                        }))}
                        placeholder={t(
                            'modelValidation.datasets.importPlaceholder',
                        )}
                        showSearch
                        value={sourceDatasetStableId}
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default ValidationDatasetImportModal
